import React from 'react';
import { X, Smartphone, BellOff, Lock, Sun } from 'lucide-react';

/**
 * LiveMobileOnboardingModal - Instructional modal shown before entering Live Mobile mode
 * 
 * Design matches mockup:
 * - MIC CHECK MILLIONAIRE logo at top
 * - White text instructions on dark background
 * - Gold "Go Mobile" button with black text
 */
const LiveMobileOnboardingModal = ({ isOpen, onClose, onGoMobile }) => {
  if (!isOpen) return null;

  const handleGoMobile = () => {
    onGoMobile();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      data-testid="live-mobile-onboarding-modal"
    >
      <div className="relative w-full max-w-sm bg-[#0a0a0a] rounded-3xl shadow-2xl border border-[#333]">
        {/* Close Button - Top Right */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-50 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition"
          data-testid="live-mobile-onboarding-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 pt-10 space-y-5">
          {/* Logo - MIC CHECK MILLIONAIRE */}
          <div className="flex justify-center">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(180deg, #C9A227 0%, #B8860B 100%)' }}
              >
                <span className="text-lg">🎤</span>
              </div>
              <span className="text-base font-bold tracking-wider" style={{ color: '#C9A227' }}>MIC CHECK<br/>MILLIONAIRE</span>
            </div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              Welcome to Live Mobile
              <Smartphone className="w-6 h-6" style={{ color: '#C9A227' }} />
            </h2>
          </div>

          {/* Description */}
          <p className="text-center text-gray-300 text-sm">
            Go live from anywhere with just your phone.<br/>
            No camera necessary.
          </p>

          {/* Pre-flight Checklist */}
          <div className="flex justify-center gap-4 py-2">
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center border border-[#333]">
                <BellOff className="w-5 h-5 text-red-400" />
              </div>
              <span className="text-[10px] text-gray-400">Do Not Disturb</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center border border-[#333]">
                <Lock className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-[10px] text-gray-400">Lock Portrait</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center border border-[#333]">
                <Sun className="w-5 h-5 text-yellow-400" />
              </div>
              <span className="text-[10px] text-gray-400">Display On</span>
            </div>
          </div>

          {/* TikTok Business Account Note */}
          <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-3">
            <p className="text-center text-xs text-gray-300">
              <span className="text-[#C9A227] font-bold">TikTok Required:</span> Switch to a Business Account (free) to go live
            </p>
          </div>

          {/* Instructions */}
          <div className="space-y-2 text-white text-sm font-medium">
            <p className="flex gap-3">
              <span className="text-[#C9A227] font-bold">1.</span>
              <span>Tap <strong>Go Mobile</strong></span>
            </p>
            <p className="flex gap-3">
              <span className="text-[#C9A227] font-bold">2.</span>
              <span><strong>Phone prep:</strong> DND • Portrait Lock • Screen On</span>
            </p>
            <p className="flex gap-3">
              <span className="text-[#C9A227] font-bold">3.</span>
              <span>TikTok → Go Live → Mobile Gaming → Screen Share</span>
            </p>
            <p className="flex gap-3">
              <span className="text-[#C9A227] font-bold">4.</span>
              <span>Switch back to <strong>MCM tab</strong></span>
            </p>
            <p className="flex gap-3">
              <span className="text-[#C9A227] font-bold">✓</span>
              <span>You're live! 🎉</span>
            </p>
          </div>

          {/* CTA Button - Rich gold with black text */}
          <button
            onClick={handleGoMobile}
            className="w-full py-4 rounded-full font-bold text-black text-lg transition shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            style={{ 
              background: 'linear-gradient(180deg, #C9A227 0%, #B8860B 50%, #A07608 100%)',
              boxShadow: '0 4px 15px rgba(201, 162, 39, 0.4)'
            }}
            data-testid="live-mobile-go-button"
          >
            Go Mobile
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveMobileOnboardingModal;
